import { ComponentSelectorResult } from '@ag-grid-community/core/dist/cjs/components/framework/userComponentFactory'
import { AgGridReact } from '@ag-grid-community/react'
import {
  AgGridEvent,
  AllModules,
  ColumnApi,
  ColumnResizedEvent,
  EditableCallbackParams,
  GridApi,
  ICellEditorParams,
  ValueFormatterParams
} from '@ag-grid-enterprise/all-modules'
import * as EvaluationLoader from '@cck/backend/dist/evaluation/EvaluationLoader'
import { setReference } from '@cck/backend/dist/evaluation/EvaluationUpdater'
import { Base, getKoEvaluation } from '@cck/common/dist/data/Evaluation'
import { parseDateStr } from '@cck/common/dist/utils/TimeUtils'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from 'antd/lib/typography'
import _ from 'lodash'
import React from 'react'

import AlertMessage, { AlertMessageHandler } from '../common/AlertMessage'
import NoticeTextView from '../common/NoticeTextView'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%'
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(2)
  }
}))

const EMPTY = '(없음)'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function closedCellStyle(params: any): any {
  if (params.data.state === '2') {
    return { backgroundColor: '#eeeeee' }
  }
}

interface GridProps {
  gridApi?: GridApi
  columnApi?: ColumnApi
}

interface Props {
  type: 'operation' | 'design'
}

const ReferenceSetting: React.FC<Props> = ({ type }) => {
  const classes = useStyles()
  const [evaluations, setEvaluations] = React.useState<Base[]>([])
  const [gridApi, setGridApi] = React.useState<GridProps>()
  const alertRef = React.useRef<AlertMessageHandler>(null)

  const columns = React.useMemo(() => {
    return [
      {
        headerName: '상태',
        field: 'state',
        valueFormatter: (params: ValueFormatterParams): string => {
          return params.value === '1' ? '진행중' : '마감'
        }
      },
      {
        headerName: '이름',
        field: 'name',
        flex: 1
      },
      {
        headerName: '통제 개수',
        field: 'controlIds',
        valueFormatter: (params: ValueFormatterParams): string => {
          return params.value.length
        }
      },
      {
        headerName: '시작일',
        field: 'startDate',
        valueFormatter: (params: ValueFormatterParams): string => {
          return parseDateStr('YYYYMMDD', params.value)
        }
      },
      {
        headerName: '종료일',
        field: 'endDate',
        valueFormatter: (params: ValueFormatterParams): string => {
          return parseDateStr('YYYYMMDD', params.value)
        }
      },
      {
        headerName: '참조 할 평가',
        field: 'refEvaluationName',
        valueFormatter: (params: ValueFormatterParams): string => params.value || EMPTY,
        editable: (params: EditableCallbackParams): boolean => params.data.state === '1',
        cellEditorSelector: (params: ICellEditorParams): ComponentSelectorResult => {
          if (params.data.state === '1') {
            return {
              component: 'agRichSelectCellEditor',
              params: {
                values: params.data.candidates
              }
            }
          }
          return {}
        }
      }
    ]
  }, [])

  React.useEffect(() => {
    EvaluationLoader.getBases(type).then((summaries: Base[]) => {
      // prettier-ignore
      const closedEvaluationNames = _(summaries)
            .filter({ state: '2' })
            .map('name')
            .value()

      closedEvaluationNames.unshift('')
      _.forEach(summaries, (summary) => {
        _.set(summary, 'candidates', closedEvaluationNames)
      })
      setEvaluations(summaries as Base[])
    })
  }, [type])

  return (
    <div className={classes.content}>
      <Typography.Title level={3}>{getKoEvaluation(type)}</Typography.Title>
      <div className="ag-theme-cck" style={{ flex: 1, position: 'relative' }}>
        <AgGridReact
          enableCellChangeFlash
          columnDefs={columns}
          defaultColDef={{
            wrapText: true,
            autoHeight: true,
            resizable: true,
            lockPosition: true,
            filter: 'agSetColumnFilter',
            suppressSizeToFit: true,
            filterParams: {
              excelMode: 'windows' // can be 'windows' or 'mac'
            },
            cellStyle: closedCellStyle
          }}
          modules={AllModules}
          rowData={_.sortBy(evaluations, ['state'])}
          onColumnResized={(params: ColumnResizedEvent) => params.api.resetRowHeights()}
          onCellValueChanged={(event) => {
            // eslint-disable-next-line no-console
            console.log('on cell value changed ', event)
            if (event.colDef.field === 'refEvaluationName') {
              setReference(type, event.data.name, event.newValue)
                .then(() => {
                  alertRef.current?.showAlert('success', '설정완료')
                })
                .catch((e) => {
                  // eslint-disable-next-line no-console
                  console.log(e)
                  alertRef.current?.showAlert('error', '설정실패')
                })
            }
          }}
          onGridReady={(event: AgGridEvent): void => {
            setGridApi({
              gridApi: event.api,
              columnApi: event.columnApi
            })
          }}
        />
      </div>
      <AlertMessage ref={alertRef} />
    </div>
  )
}

const ReferenceSettings: React.FC = () => {
  const classes = useStyles()
  return (
    <div className={classes.root}>
      <NoticeTextView>
        참조 설정된 평가가 있을 경우, 수행요청 메일을 전송시에 참조 설정된 평가의 파일을 함께
        전송합니다.
      </NoticeTextView>
      <ReferenceSetting type="operation" />
      <ReferenceSetting type="design" />
    </div>
  )
}

export default ReferenceSettings
